import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

/**
 * PMMonthlyBarChart
 * - Supports ANY labels length (3, 6, 12...)
 * - Single dataset bar chart (non-stacked)
 * - Styling close to your existing UI
 */
export default function PMMonthlyBarChart({
  title = "Monthly Trends",
  labels = [],
  data = [],
  maxValue = 8,
  containerStyle = { height: "260px", width: "100%" },
  barColor = "#008CFF",
  showTickSeparators = true,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const addSeparators = () => {
    if (!showTickSeparators || !chartRef.current || !containerRef.current)
      return;

    // remove existing
    containerRef.current
      .querySelectorAll(".chart-tick-separator")
      .forEach((el) => el.remove());

    const xAxis = chartRef.current.scales.x;
    if (!xAxis?.ticks?.length) return;

    // Add small vertical ticks at bottom like your current chart
    for (let i = 0; i < xAxis.ticks.length; i++) {
      const x = xAxis.getPixelForTick(i) - xAxis.getPixelForTick(0) + 8;

      const bottomPosition = xAxis.height - 15;

      const separator = document.createElement("div");
      separator.className = "chart-tick-separator";
      separator.style.cssText = `
        position: absolute;
        left: ${x}px;
        bottom: ${bottomPosition}px;
        width: 1px;
        height: 9px;
        background-color: #B5BBC0;
        pointer-events: none;
      `;
      containerRef.current.appendChild(separator);
    }

    // Extra end separator
    if (xAxis.ticks.length > 1) {
      const last = xAxis.getPixelForTick(xAxis.ticks.length - 1);
      const step = xAxis.getPixelForTick(1) - xAxis.getPixelForTick(0);
      const extraX = last + step;

      const extra = document.createElement("div");
      extra.className = "chart-tick-separator";
      extra.style.cssText = `
        position: absolute;
        left: ${extraX - xAxis.getPixelForTick(0) + 8}px;
        bottom: ${xAxis.height - 15}px;
        width: 1px;
        height: 9px;
        background-color: #B5BBC0;
        pointer-events: none;
      `;
      containerRef.current.appendChild(extra);
    }
  };

  const destroyChart = () => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.update();
        setTimeout(addSeparators, 150);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      destroyChart();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    destroyChart();

    const ctx = canvasRef.current.getContext("2d");

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: title,
            data,
            backgroundColor: barColor,
            borderColor: "#121E29",
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          onComplete: () => setTimeout(addSeparators, 0),
        },
        plugins: {
          legend: { display: false },
          title: {
            display: true,
            text: title,
            padding: { bottom: 16 },
            font: {
              family: "Poppins",
              size: 20,
              weight: "600",
              lineHeight: 1.5,
            },
            align: "start",
            color: "#FFFFFF",
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (ctx) => `${ctx.parsed.y ?? 0}`,
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: "#B5BBC0",
              font: {
                family: "Poppins",
                weight: 600,
                size: 14,
                lineHeight: 1.5,
              },
            },
            grid: { display: false },
            border: { color: "#2B3C4A", width: 1 },
          },
          y: {
            min: 0,
            max: maxValue,
            ticks: { display: false },
            grid: { display: false },
            border: { display: false },
          },
        },
      },
    });

    setTimeout(addSeparators, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, JSON.stringify(labels), JSON.stringify(data), maxValue, barColor]);

  return (
    <div ref={containerRef} style={{ position: "relative", ...containerStyle }}>
      <canvas ref={canvasRef} />
    </div>
  );
}
